import React, { useState } from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  BodyText,
  Button,
  Stack,
} from '../../../ui';
import { BasicStarFilledIcon } from '../../../ui/Icons';
import StateLink from '../../../StateLink';
import { ICrossPromotedProvider } from './types';
import formatPriceAndDuration from '../../../../modules/formatters/formatPriceAndDuration';
import { STATIC_ASSET_URL } from '../../../../config';
import AvatarIcon from '../../../AvatarIcon';
import { NewProBadge } from '../../NewPro/NewProBadge';
import { NEW_PRO_DAYS, NEW_PRO_MIN_REVIEWS } from '../../NewPro/NewProConstants';
import { TopProBadge } from '../../TopPro/TopProBadge';
import { daysSince } from '../../../provider/TopPro/dateUtils';
import useAsyncEffect from '../../../../../../packages/hooks/src/useAsyncEffect';
import { getProProfileClientView, ClientProviderProfile } from '../../../../api/ProfileClientView/ProfileClientView';
import { useIsMobile } from '../../../../hooks';

const ProviderCard = ({
  provider,
  onClick,
  appearance,
  utmSource,
  showService = true,
  showProfession = false,
}: {
  provider: ICrossPromotedProvider;
  onClick?: (provider: ICrossPromotedProvider) => void;
  appearance: 'large' | 'medium' | 'small';
  utmSource: string;
  showService?: boolean;
  showProfession?: boolean;
}) => {
  const isSmall = appearance === 'small';
  const isMobile = useIsMobile();
  const [publicProvider, setPublicProvider] = useState<ClientProviderProfile | null>(null);
  let url: string;
  if (provider.profile_photo?.match(/^http/)) {
    if (provider.profile_photo?.match(/_[0-9]+x[0-9]+.[a-z]{2,6}$/)) {
      url = provider.profile_photo;
    } else {
      url = `${provider.profile_photo}_300x300.jpg`;
    }
  } else {
    url = `${STATIC_ASSET_URL}/${provider.profile_photo}_300x300.jpg`;
  }
  const linkParams = {
    providerId: provider.vanity_url,
    proId: provider.provider_id,
    ncd_term: provider.provider_id,
    ncd_source: utmSource,
  };

  useAsyncEffect(async () => {
    const pro = await getProProfileClientView(provider.provider_id) as ClientProviderProfile;
    setPublicProvider(pro);
  }, [provider?.provider_id]);

  const isLargeName = provider.name.length >= 27;
  const isNewPro = publicProvider && daysSince(publicProvider?.creation_time) <= NEW_PRO_DAYS;
  const isTopPro = publicProvider && publicProvider?.is_top_pro;

  return (
    <Box
      testID={`cross_promotion_modal_provider_${provider.service_id}`}
      paddingY={8}
      paddingX={32}
    >
      <StateLink
        to="profile"
        params={linkParams}
        onClick={() => onClick?.(provider)}
        testID="vanity_state_link"
      >
        <Box
          padding={8}
          borderWidth={1}
          borderColor={BrandColor.LightGrey}
          borderStyle="solid"
          color={BrandColor.WhiteWash}
          borderRadius={8}
        >
          <Inline>
            <Box paddingRight={16}>
              <Box borderColor={BrandColor.LightGrey} borderWidth={1}>
                <AvatarIcon
                  testID="provider-card-image"
                  icon={url}
                  sizeClass="medium-small"
                />
              </Box>
            </Box>
            <Box flex={1}>
              {isNewPro && !isTopPro && (
                <Box width="100%" paddingBottom={12}>
                  <Box width={isMobile ? '65%' : '45%'} maxWidth={150}>
                    <NewProBadge
                      color={BrandColor.FloraLight}
                      borderRadius={100}
                      iconColor={BrandColor.FloraDark}
                      top={4}
                    />
                  </Box>
                </Box>
              )}
              {isTopPro && (
                <Box width="100%" paddingBottom={12}>
                  <Box width={isMobile ? '40%' : '25%'} maxWidth={100}>
                    <TopProBadge top={4} />
                  </Box>
                </Box>
              )}
              <Inline space={6}>
                <BodyText testID="pro_name" style={{ fontSize: 14 }} weight="bold">{provider.name}</BodyText>
                { !isLargeName && provider.num_ratings >= NEW_PRO_MIN_REVIEWS && (
                  <React.Fragment>
                    <Box paddingTop={2}>
                      <BasicStarFilledIcon color={BrandColor.Metal} height={16} width={16} />
                    </Box>
                    <BodyText color={BrandColor.Metal} weight="bold" style={{ fontSize: 12 }}>
                      {provider.average_rating.toFixed(1)}
                    </BodyText>
                    {provider.num_ratings > 5 && (
                      <BodyText color={BrandColor.Metal} style={{ fontSize: 12 }}>
                        {`(${provider.num_ratings})`}
                      </BodyText>
                    )}
                  </React.Fragment>
                )}
              </Inline>
              { isLargeName && provider.num_ratings >= 5 && (
                <Inline space={8}>
                  <Box paddingTop={2}>
                    <BasicStarFilledIcon color={BrandColor.Metal} height={16} width={16} />
                  </Box>
                  <BodyText color={BrandColor.Metal} weight="bold" style={{ fontSize: 12 }}>
                    {provider.average_rating.toFixed(1)}
                  </BodyText>
                  {provider.num_ratings > 5 && (
                    <BodyText color={BrandColor.Metal} style={{ fontSize: 12 }}>
                      {`(${provider.num_ratings})`}
                    </BodyText>
                  )}
                </Inline>
              )}
              <Stack space={6} paddingY={4}>
                <BodyText color={BrandColor.BackInBlack} testID="service_name">{provider.service_name}</BodyText>
                <BodyText testID="price_and_duration" color={BrandColor.Metal}>
                  {formatPriceAndDuration(provider, provider.show_and_up)}
                </BodyText>
              </Stack>
              {(!isSmall && (showService || showProfession)) && (
                <Inline justifyContent="flex-start" paddingTop={8}>
                  <Button testID="learn_more" size="small">Learn More</Button>
                </Inline>
              )}
            </Box>
            {(isSmall || !(showService || showProfession)) && (
              <Box justifyContent="flex-end" paddingBottom={16} paddingRight={4}>
                <Button testID="learn_more" size="small">
                  {showService ? 'details' : 'Learn More'}
                </Button>
                {!showService && <Box paddingBottom={12} />}
              </Box>
            )}
          </Inline>
        </Box>
      </StateLink>
    </Box>
  );
};

export default ProviderCard;
